'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { LOCALE_CONFIG, SUPPORTED_LOCALES, type SiteLocale } from '@/lib/i18n/locales'
import { getLanguageSwitchPath, getLocaleFromPathname } from '@/lib/i18n/paths'

type LanguageSwitcherProps = {
  className?: string
  variant?: 'floating' | 'inline'
}

type LocaleFlagAsset = {
  src: string
  alt: string
}

const LOCALE_FLAGS: Record<SiteLocale, LocaleFlagAsset> = {
  es: { src: '/flags/nucleo/countries/es.svg', alt: 'Spain flag' },
  en: { src: '/flags/nucleo/countries/us.svg', alt: 'United States flag' },
  fr: { src: '/flags/nucleo/countries/fr.svg', alt: 'France flag' },
  de: { src: '/flags/nucleo/countries/de.svg', alt: 'Germany flag' },
  it: { src: '/flags/nucleo/countries/it.svg', alt: 'Italy flag' },
  pt: { src: '/flags/nucleo/countries/pt.svg', alt: 'Portugal flag' },
  nl: { src: '/flags/nucleo/countries/nl.svg', alt: 'Netherlands flag' },
  ro: { src: '/flags/nucleo/countries/ro.svg', alt: 'Romania flag' },
  ar: { src: '/flags/nucleo/countries/sa.svg', alt: 'Saudi Arabia flag' },
}

export default function LanguageSwitcher({ className = '', variant = 'floating' }: LanguageSwitcherProps) {
  const router = useRouter()
  const pathname = usePathname() || '/'
  const locale = getLocaleFromPathname(pathname)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const options = useMemo(
    () =>
      SUPPORTED_LOCALES.map((item) => ({
        locale: item,
        label: LOCALE_CONFIG[item].label,
        nativeName: LOCALE_CONFIG[item].nativeName,
        flag: LOCALE_FLAGS[item],
      })),
    [],
  )

  const selected = options.find((item) => item.locale === locale) ?? options[0]

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEsc)
    }
  }, [])

  const panelClassName =
    variant === 'inline'
      ? 'mt-2 w-full max-h-[320px] overflow-y-auto rounded-xl border border-cream-200/25 bg-gradient-to-b from-gray-900/95 to-gray-950/95 shadow-[0_18px_44px_rgba(2,6,23,0.7),0_0_0_1px_rgba(253,230,138,0.14)] backdrop-blur-xl'
      : 'absolute right-0 top-full z-40 mt-2 min-w-[260px] max-h-[320px] overflow-y-auto rounded-xl border border-cream-200/25 bg-gradient-to-b from-gray-900/95 to-gray-950/95 shadow-[0_18px_44px_rgba(2,6,23,0.7),0_0_0_1px_rgba(253,230,138,0.14)] backdrop-blur-xl'

  const buttonClassName = `inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream-300 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 ${
    variant === 'inline' ? 'w-full justify-center' : ''
  } ${
    open
      ? 'border-cream-300/85 bg-gray-900/85 text-cream-100 shadow-[0_0_0_1px_rgba(253,230,138,0.35),0_0_18px_rgba(252,211,77,0.22)]'
      : 'border-gray-600 bg-gray-900/70 text-gray-100 hover:border-cream-400/80 hover:text-cream-100 hover:shadow-[0_0_16px_rgba(252,211,77,0.12)]'
  }`

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex ${className}`.trim()}
    >
      <button
        type="button"
        aria-label="Cambiar idioma"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={buttonClassName}
      >
        <span className="inline-flex items-center" aria-hidden="true">
          <Image
            src={selected.flag.src}
            alt={selected.flag.alt}
            width={22}
            height={16}
            className="h-4 w-[22px] rounded-[3px] border border-gray-600/60 object-cover"
          />
        </span>
      </button>

      {open && (
        <div className={panelClassName} style={{ animation: 'language-dropdown-in 140ms ease-out' }}>
          <ul className="p-1.5">
            {options.map((item) => {
              const isSelected = item.locale === locale
              return (
                <li key={item.locale}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false)
                      router.push(getLanguageSwitchPath(pathname, item.locale))
                    }}
                    className={`flex w-full items-center justify-between rounded-lg border px-2.5 py-2 text-left transition-all duration-150 ${
                      isSelected
                        ? 'border-cream-300/35 bg-gradient-to-r from-cream-300/20 to-amber-300/10 text-cream-100'
                        : 'border-transparent text-gray-200 hover:border-gray-700 hover:bg-gray-800/80 hover:text-gray-100'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Image
                        src={item.flag.src}
                        alt={item.flag.alt}
                        width={20}
                        height={15}
                        className="h-[15px] w-5 rounded-[3px] border border-gray-600/70 object-cover"
                      />
                      <span className="text-xs font-semibold normal-case">
                        {item.label} · {item.nativeName}
                      </span>
                    </span>
                    {isSelected && (
                      <svg className="h-4 w-4 text-cream-200" viewBox="0 0 16 16" aria-hidden="true">
                        <path className="fill-current" d="M6.567 12.24 2.326 8l1.414-1.414 2.827 2.826 5.693-5.693 1.414 1.414-7.107 7.107Z" />
                      </svg>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <style jsx>{`
        @keyframes language-dropdown-in {
          0% {
            opacity: 0;
            transform: translateY(-4px) scale(0.985);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  )
}
